import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { DripCampaign } from '@feathr/blackbox';
import { Alert, Checkbox, Skeleton, Tooltip } from '@feathr/components';

const MAX_ENROLL_COUNT = 200000;

import * as styles from './DripBulkEnroll.css';

interface IProps {
  campaign: DripCampaign;
  peopleCount: number;
  hasErrors: boolean;
  isCountPending?: boolean;
}

function DripBulkEnroll({
  campaign,
  peopleCount,
  hasErrors,
  isCountPending = false,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [shouldShowAlert, setShouldShowAlert] = useState(false);
  const [overLimit, setOverLimit] = useState(false);
  const sendAll = campaign.get('send_all');

  function handleSetBulkEnroll(newValue?: boolean): void {
    campaign.set({ send_all: newValue });
  }

  useEffect(() => {
    if (hasErrors) {
      campaign.set({ send_all: false });
      return;
    }

    /*
     * Mid-recount `peopleCount` still holds the previous filters' answer. Evaluating the limit
     * against it would latch the over-limit warning, and uncheck bulk enrollment, off a number
     * that is about to be replaced.
     */
    if (isCountPending) {
      return;
    }

    if (peopleCount > MAX_ENROLL_COUNT) {
      // If the state was checked, we show the alert.
      if (sendAll === true) {
        setShouldShowAlert(true);
      }

      // Set campaign.send_all to false if over limit.
      handleSetBulkEnroll(false);
      setOverLimit(true);
    } else {
      setOverLimit(false);
      setShouldShowAlert(false);
    }
  }, [peopleCount, hasErrors, isCountPending]);

  /*
   * Mid-recount nothing about the limit is known yet, so the verdict, its warning, and the
   * "adjust filters" hint all stay off screen until the new count lands rather than being
   * reported off the previous filters' answer.
   */
  const isOverLimit = !isCountPending && overLimit;
  const isAlertVisible = !isCountPending && shouldShowAlert;
  const isLimitHintVisible = !isCountPending && !shouldShowAlert && peopleCount >= MAX_ENROLL_COUNT;

  const checkbox = (
    <Checkbox
      data-name={'checkbox-drip-bulk-single-send'}
      disabled={peopleCount === 0 || isOverLimit || hasErrors || isCountPending}
      label={
        <Flex align={'center'} gap={theme.spacing['2']}>
          {isCountPending ? (
            <Trans t={t}>
              Enroll the <Skeleton width={'6ch'} /> people who already meet these criteria.
            </Trans>
          ) : (
            t('Enroll the {{ count, number }} person who already meets these criteria.', {
              count: peopleCount > 0 && !hasErrors ? peopleCount : 0,
            })
          )}
          <Tooltip
            position={'top'}
            title={t(
              'Bulk-enrolled contacts will receive their first email at the scheduled start time of this campaign, irrespective of the delay timer, which only applies to additional contacts who fulfill the trigger conditions moving forward.',
            )}
          >
            <span className={styles.infoIcon}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          </Tooltip>
        </Flex>
      }
      layout={'well'}
      name={'drip_bulk_single_send'}
      onChange={handleSetBulkEnroll}
      value={sendAll}
    />
  );

  const element = isLimitHintVisible ? (
    <Tooltip
      className={styles.tooltip}
      position={'bottom'}
      title={t('Adjust filters to include fewer than {{number}} people.', {
        number: MAX_ENROLL_COUNT,
      })}
    >
      {checkbox}
    </Tooltip>
  ) : (
    checkbox
  );

  return (
    <div className={styles.root}>
      {element}
      {isAlertVisible && (
        <Alert
          className={styles.alert}
          description={t(
            'Adjust your filters to include fewer than {{number}} people. Publishing with current filters will only enroll new people who meet these criteria going forward. No bulk enrollment will occur.',
            { number: MAX_ENROLL_COUNT },
          )}
          title={t('Enrollment limit exceeded')}
          type={'warning'}
        />
      )}
    </div>
  );
}

export default observer(DripBulkEnroll);
